import type { AdminAsset, AdminPageBlock } from '@diana/contracts';
import { Plus } from 'lucide-react';
import { CompactRichTextField } from '../../components/CompactRichTextField.js';
import { plainToRichText } from '../../components/RichTextField.js';
import { Button } from '../../ui/index.js';
import { ContextualMediaField } from '../media/ContextualMediaField.js';
import { TextField, TextAreaField } from '../settings/SettingsEditor.js';

import { EditorFieldGroup, EditorCollectionItem } from './PageEditorFields.js';

type Hero = Extract<AdminPageBlock, { type: 'Hero' }>;
type Feature = Extract<AdminPageBlock, { type: 'Feature' }>;
const defaultForm = {
  heading: 'Gift a',
  emphasis: 'photoshoot',
  intro: 'Tell me who the gift is for. I’ll help you choose the session and arrange the details.',
  messageLabel: 'Your message',
  recipientLabel: 'Who is it for?',
  submitLabel: 'Send request',
  successMessage: 'Thank you! I’ll be in touch to help arrange your gift card.',
};

export function GiftHeroFields({
  block,
  assets,
  onChange,
}: {
  block: Hero;
  assets: readonly AdminAsset[];
  onChange(block: Hero): void;
}) {
  const p = block.payload;
  const update = (patch: Partial<typeof p>) => onChange({ ...block, payload: { ...p, ...patch } });
  const card = p.certificate ?? {
    label: 'Gift card',
    titleLines: [p.title],
    metaLines: ['A photography experience'],
    signature: 'with Diana Perova',
  };
  return (
    <div className="home-fields">
      <EditorFieldGroup title="Page content">
        <TextField
          label="Heading"
          value={[p.title, p.accent].filter(Boolean).join(' ')}
          onChange={(title) => update({ title, accent: undefined })}
        />
        <CompactRichTextField
          label="Description"
          value={p.subtitle ?? plainToRichText(p.lead ?? '')}
          onChange={(subtitle) => update({ subtitle, lead: undefined })}
        />
        <TextField
          label="Button label"
          value={p.action?.label ?? 'Get a gift card'}
          onChange={(label) =>
            update({
              action: { ...(p.action ?? { href: '/gift', style: 'Primary' as const }), label },
            })
          }
        />
      </EditorFieldGroup>
      <EditorFieldGroup title="Gift card photograph">
        <ContextualMediaField
          title="Gift card photograph"
          profile="standard"
          cropProfile="portrait-4-5"
          value={p.media}
          assets={assets}
          onChange={(media) => update({ media, slides: undefined })}
        />
      </EditorFieldGroup>
      <EditorFieldGroup title="Card text">
        <TextField
          label="Gift card heading"
          value={card.label}
          onChange={(label) => update({ certificate: { ...card, label } })}
        />
        {card.titleLines.map((line, i) => (
          <TextField
            key={i}
            label={`Recipient line ${i + 1}`}
            value={line}
            onChange={(value) =>
              update({
                certificate: {
                  ...card,
                  titleLines: card.titleLines.map((v, j) => (i === j ? value : v)),
                },
              })
            }
          />
        ))}
        {card.metaLines.map((line, i) => (
          <TextField
            key={i}
            label={`Experience line ${i + 1}`}
            value={line}
            onChange={(value) =>
              update({
                certificate: {
                  ...card,
                  metaLines: card.metaLines.map((v, j) => (i === j ? value : v)),
                },
              })
            }
          />
        ))}
        <TextField
          label="Photographer line"
          value={card.signature}
          onChange={(signature) => update({ certificate: { ...card, signature } })}
        />
      </EditorFieldGroup>
    </div>
  );
}

export function GiftInquiryFields({
  block,
  onChange,
}: {
  block: Hero;
  onChange(block: Hero): void;
}) {
  const form = block.payload.inquiry ?? defaultForm;
  const labels: Partial<Record<keyof typeof form, string>> = {
    heading: 'Form heading',
    emphasis: 'Italic text',
    intro: 'Form introduction',
    messageLabel: 'Message label',
    submitLabel: 'Submit button label',
    successMessage: 'Success message',
  };
  const field = (key: keyof typeof form) => {
    const props = {
      label: labels[key]!,
      value: form[key],
      onChange: (value: string) =>
        onChange({ ...block, payload: { ...block.payload, inquiry: { ...form, [key]: value } } }),
    };
    return key === 'intro' || key === 'successMessage' ? (
      <TextAreaField key={key} {...props} autoGrow />
    ) : (
      <TextField key={key} {...props} />
    );
  };
  return (
    <div className="home-fields">
      <EditorFieldGroup title="Form text">
        {(['heading', 'emphasis', 'intro'] as const).map(field)}
      </EditorFieldGroup>
      <EditorFieldGroup title="Labels and confirmation">
        {(['messageLabel', 'submitLabel', 'successMessage'] as const).map(field)}
      </EditorFieldGroup>
    </div>
  );
}

export function GiftSessionsFields({
  block,
  assets,
  onChange,
}: {
  block: Feature;
  assets: readonly AdminAsset[];
  onChange(block: Feature): void;
}) {
  const p = block.payload;
  const items = p.items ?? [];
  const update = (patch: Partial<typeof p>) => onChange({ ...block, payload: { ...p, ...patch } });
  const updateItem = (index: number, patch: Partial<(typeof items)[number]>) =>
    update({ items: items.map((item, i) => (i === index ? { ...item, ...patch } : item)) });
  return (
    <div className="home-fields">
      <EditorFieldGroup title="Section introduction">
        <TextField label="Heading" value={p.heading} onChange={(heading) => update({ heading })} />
        <CompactRichTextField
          label="Introduction"
          value={p.body}
          onChange={(body) => update({ body })}
        />
      </EditorFieldGroup>
      <EditorFieldGroup title="Sessions">
        {items.map((item, i) => (
          <EditorCollectionItem
            key={item.key}
            label={`Session ${i + 1}`}
            title={item.title}
            index={i}
            count={items.length}
            onMove={(direction) => {
              const next = [...items];
              [next[i], next[i + direction]] = [next[i + direction]!, next[i]!];
              update({ items: next });
            }}
            onRemove={() => update({ items: items.filter((_, j) => i !== j) })}
          >
            <TextField
              label={`Session ${i + 1} title`}
              value={item.title}
              onChange={(title) => updateItem(i, { title })}
            />
            <CompactRichTextField
              label={`Session ${i + 1} description`}
              value={item.body ?? plainToRichText('')}
              onChange={(body) => updateItem(i, { body })}
            />
            <ContextualMediaField
              title={`Session ${i + 1} photograph`}
              profile="standard"
              cropProfile="portrait-4-5"
              value={item.media}
              assets={assets}
              onChange={(media) => updateItem(i, { media })}
            />
          </EditorCollectionItem>
        ))}
        <Button
          variant="outline"
          leadingIcon={Plus}
          disabled={items.length >= 3}
          onClick={() =>
            update({
              items: [
                ...items,
                {
                  key: crypto.randomUUID(),
                  title: 'New session',
                  body: plainToRichText('Describe this gift.'),
                },
              ],
            })
          }
        >
          Add session
        </Button>
      </EditorFieldGroup>
      <EditorFieldGroup title="Explore link">
        <TextField
          label="Explore link label"
          value={p.action?.label ?? 'Explore the sessions'}
          onChange={(label) => update({ action: { label, href: '/pricing', style: 'Secondary' } })}
        />
      </EditorFieldGroup>
    </div>
  );
}
